// src/services/clarityService.ts
// Microsoft Clarity integration service

import clarity from '@microsoft/clarity';

/**
 * Funnel events the portal emits. Names are snake_case so they are easy to
 * filter in the Clarity dashboard (Filters → Custom events) and cannot be
 * confused with Clarity's own "smart events" (SignUp, Download, Login…).
 *
 * A detail suffix is appended with a colon, e.g. `download_click:windows`,
 * `cta_click:hero_create_account`, `checkout_start:captain`.
 */
export type FunnelEvent =
  | 'cta_click'
  | 'download_click'
  | 'npm_copy'
  | 'register_start'
  | 'register_success'
  | 'register_failed'
  | 'oauth_signup_success'
  | 'oauth_login_success'
  | 'oauth_failed'
  | 'login_success'
  | 'login_failed'
  | 'session_expired'
  | 'verify_email_success'
  | 'pricing_plan_click'
  | 'checkout_start'
  | 'checkout_resumed'
  | 'payg_click'
  | 'first_run_view'
  | 'first_run_step'
  | 'api_key_created';

/** Hosts where Clarity must stay off: local dev traffic polluted the project. */
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0', '[::1]']);

/**
 * Clarity Service
 * Provides a wrapper around Microsoft Clarity for behavioral analytics
 */
class ClarityService {
  private initialized: boolean = false;
  private projectId: string | null = null;
  /** Events fired before init (e.g. hero click during bootstrap) are replayed once ready. */
  private pending: string[] = [];

  /**
   * Initialize Microsoft Clarity
   * @param projectId - Clarity project ID from environment variables
   * @param surface - which product surface this build is (tagged on every session)
   */
  initialize(projectId: string, surface?: string): void {
    // Only initialize if not already done and projectId is valid
    if (this.initialized || !projectId || projectId.trim() === '') {
      return;
    }

    // Local dev instances share the production project id via .env — keep
    // them out of the dashboard unless explicitly forced for testing.
    if (typeof window !== 'undefined' && LOCAL_HOSTS.has(window.location.hostname)) {
      if (import.meta.env.VITE_CLARITY_FORCE !== 'true') {
        console.log('[Clarity] Skipped on local host (set VITE_CLARITY_FORCE=true to enable)');
        return;
      }
    }

    try {
      clarity.init(projectId);
      this.projectId = projectId;
      this.initialized = true;
      console.log('✅ Microsoft Clarity initialized:', projectId);
      if (surface) this.setTag('surface', surface);
      this.pending.splice(0).forEach((name) => this.event(name));
    } catch (error) {
      console.error('❌ Failed to initialize Microsoft Clarity:', error);
    }
  }

  /**
   * Check if Clarity is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Identify a user in Clarity
   * @param userId - Unique user identifier (will be hashed by Clarity)
   * @param customSessionId - Optional custom session ID
   * @param customPageId - Optional custom page ID
   * @param friendlyName - Optional friendly name for the user
   */
  identify(
    userId: string,
    customSessionId?: string,
    customPageId?: string,
    friendlyName?: string
  ): void {
    if (!this.initialized) return;

    try {
      clarity.identify(userId, customSessionId, customPageId, friendlyName);
    } catch (error) {
      console.error('❌ Failed to identify user in Clarity:', error);
    }
  }

  /**
   * Set a custom tag in Clarity
   * @param key - Tag key
   * @param value - Tag value (string or array of strings)
   */
  setTag(key: string, value: string | string[]): void {
    if (!this.initialized) return;

    try {
      clarity.setTag(key, value);
    } catch (error) {
      console.error('❌ Failed to set Clarity tag:', error);
    }
  }

  /**
   * Track a custom event in Clarity
   * @param eventName - Name of the event to track
   */
  event(eventName: string): void {
    if (!this.initialized) {
      // Keep a short replay buffer so early clicks are not lost while the
      // tracker boots; on local hosts this is simply a console trace.
      if (this.pending.length < 20) this.pending.push(eventName);
      if (import.meta.env.DEV) console.log(`📊 [Clarity:dev] event: ${eventName}`);
      return;
    }

    try {
      clarity.event(eventName);
    } catch (error) {
      console.error('❌ Failed to track Clarity event:', error);
    }
  }

  /**
   * Track a funnel event with an optional detail suffix.
   * `track('download_click', 'windows')` → event `download_click:windows`.
   */
  track(name: FunnelEvent, detail?: string): void {
    const suffix = detail ? `:${String(detail).toLowerCase().replace(/[^a-z0-9_.-]+/g, '_')}` : '';
    this.event(`${name}${suffix}`);
  }

  /**
   * Set cookie consent status
   * @param hasConsent - Whether user has given consent (default: true)
   */
  consent(hasConsent: boolean = true): void {
    if (!this.initialized) return;

    try {
      clarity.consent(hasConsent);
    } catch (error) {
      console.error('❌ Failed to set Clarity consent:', error);
    }
  }

  /**
   * Upgrade session priority for important sessions
   * @param reason - Reason for upgrading the session
   */
  upgrade(reason: string): void {
    if (!this.initialized) return;

    try {
      clarity.upgrade(reason);
    } catch (error) {
      console.error('❌ Failed to upgrade Clarity session:', error);
    }
  }
}

// Export singleton instance
export const clarityService = new ClarityService();

// Export type for TypeScript
export default clarityService;
